import { keyframes, Text } from '@chakra-ui/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import type { Tab } from '@revelio/data-access';
import { Button, Callout, Spinner } from '@revelio/ui';

import { AIAssistantSparkleDisc } from '../CustomDash/components/AIAssistant/AIAssistantSparkleDisc';
import type { GlobalAssistantAccessDecision } from './global-ai-assistant-access';
import { getGlobalAssistantRouteAccess } from './global-ai-assistant-access';
import type { GlobalAssistantSuggestMatch } from './global-ai-assistant-api';
import { GlobalAIAssistantChartPreviewSlot } from './global-ai-assistant-chart-preview-slot';
import styles from './global-ai-assistant-drawer.module.css';
import { GlobalAssistantPermissionUpsell } from './global-ai-assistant-permission-upsell';
import { getStandardPageMatchForOptionId } from './global-ai-assistant-standard-page-match';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';
import {
  GEO_TAX_OFFER_TRIGGER_TESTID,
  parseGeoTaxOfferTargetId,
} from './global-ai-geo-taxonomy-switch';
import layoutStyles from './global-ai-transcript-messages.module.css';
import {
  hasOfferMarker,
  matchReportOfferByMarker,
  type OfferKind,
  type OfferSegment,
  parseOfferSegments,
  untargetedReportPosition,
} from './offer-marker';
import type { ReasoningTrace as ReasoningTraceType } from './reasoning-trace';
import { stepStatusMark } from './reasoning-trace';
import {
  buildReportOfferLaunchKey,
  type ReportOfferCardFields,
} from './report-offer-launch-key';

// Downgrades already warned about. This runs in the RENDER phase of a `.map`,
// so without it one downgraded marker re-warns on every transcript flush (and
// twice per render under StrictMode), drowning the signal it exists to give.
// Bounded by the number of distinct downgraded markers in a session.
const seenDowngrades = new Set<string>();

// Offer marker whose backing action can't render: plain text (never a dead
// trigger), stamped with `data-offer-downgraded` so a harness can tell a
// DOWNGRADE from intentional prose — they render identically. The warning is a
// local debugging aid; the attribute is the contract, and it is unconditional
// on purpose because it is asserted against deployed builds too.
const downgradedOfferSpan = (
  messageId: string,
  seg: Extract<OfferSegment, { type: 'offer' }>,
  segIdx: number
) => {
  if (process.env.NODE_ENV === 'development') {
    const key = `${messageId}|${seg.offer}|${seg.text}`;
    if (!seenDowngrades.has(key)) {
      seenDowngrades.add(key);
      console.warn(
        '[global-ai] offer marker downgraded to plain text (no backing action):',
        messageId,
        seg.offer,
        seg.text
      );
    }
  }
  return (
    <span key={segIdx} data-offer-downgraded={seg.offer}>
      {seg.text}
    </span>
  );
};

const ASSISTANT_ICON_SIZE = '21.56px';

/**
 * Pulse for an inline report trigger whose launch (prefill extraction +
 * report-builder navigation) is in flight — the async gap is long enough that
 * a static link reads as "nothing happened". Mirrors the cards' isLoading.
 */
const reportTriggerLaunchPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export function AssistantAvatar() {
  // Shared with the floating FAB so the two icons can never drift: same
  // themed circle + sparkle glyph, ink swapping per theme.
  return <AIAssistantSparkleDisc size={ASSISTANT_ICON_SIZE} aria-hidden />;
}

function AssistantAvatarSpacer() {
  return <div className={layoutStyles.avatarSpacer} aria-hidden />;
}

function AssistantAvatarSlot({ show }: { show: boolean }) {
  return show ? <AssistantAvatar /> : <AssistantAvatarSpacer />;
}

/**
 * Live model-reasoning bubble: streamed `thinking` text in a compact, scrollable
 * box that auto-follows the latest chunk while collapsed, and toggles to a taller
 * view on click. Muted/italic so it reads as reasoning, not the answer.
 */
// `bare` drops the bubble's own left rule/indent when it is nested inside the
// reasoning trace (which already supplies a left border), avoiding a double rule.
function ThinkingBubble({ text, bare }: { text: string; bare?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // `text` is a trigger dependency: re-scroll to the latest chunk as it streams.
  // biome-ignore lint/correctness/useExhaustiveDependencies: text drives the re-scroll
  useEffect(() => {
    if (!expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [text, expanded]);
  return (
    <div
      ref={scrollRef}
      onClick={() => setExpanded((prev) => !prev)}
      className={classNames(
        layoutStyles.thinkingBubble,
        !bare && styles.thinkingBorder
      )}
      data-bare={bare || undefined}
      data-expanded={expanded || undefined}
      title={expanded ? 'Click to collapse' : 'Click to expand'}
    >
      <Text
        fontSize="var(--ui-font-size-x-sm)"
        lineHeight="15px"
        className={styles.text}
        fontStyle="italic"
        opacity={0.55}
        whiteSpace="pre-wrap"
      >
        {text}
      </Text>
    </div>
  );
}

function ReasoningTraceView({ trace }: { trace: ReasoningTraceType }) {
  const running = trace.endedAt === undefined;
  const [expanded, setExpanded] = useState(true);
  // Collapse automatically once the turn finishes.
  useEffect(() => {
    if (!running) setExpanded(false);
  }, [running]);
  const seconds = trace.endedAt
    ? Math.max(1, Math.round((trace.endedAt - trace.startedAt) / 1000))
    : null;
  return (
    <div>
      <button
        onClick={() => setExpanded((p) => !p)}
        // Single-line header sits beside the avatar in a top-aligned row;
        // matching the line box to the avatar height centers them optically.
        // display=block: as an inline-level box the wrapper's 16px/1.5 strut
        // baseline-shifts the button ~2.5px down.
        className={classNames(styles.text, layoutStyles.traceToggle)}
        // Turn-state signal for tests: the bottom status spinner is suppressed
        // while a trace is active, so `data-running` is the reliable "turn
        // settled" hook. The pw-e2e settle helper keys on this testid — keep
        // them in sync.
        data-testid="global-ai-reasoning-trace"
        data-running={running ? 'true' : 'false'}
      >
        {running ? 'Working…' : `Worked for ${seconds}s`} {expanded ? '▾' : '▸'}
      </button>
      {expanded && (
        <div
          className={classNames(styles.thinkingBorder, layoutStyles.traceSteps)}
        >
          {trace.steps.map((s) => (
            <div className={layoutStyles.traceStep} key={s.id}>
              <div className={layoutStyles.stepTitle}>
                {s.status === 'running' ? (
                  <Spinner className={layoutStyles.stepSpinner} size="xs" />
                ) : (
                  <Text
                    as="span"
                    fontWeight="medium"
                    className={styles.text}
                    // The mark a step ENDED on, for tests: the glyphs are a
                    // rendering detail, and a spec asserting on the character
                    // would go red on a typography change while a step that
                    // refused and drew a tick stayed green.
                    data-testid="global-ai-reasoning-step-status"
                    data-status={s.status}
                  >
                    {stepStatusMark(s.status)}
                  </Text>
                )}
                <Text fontWeight="medium" className={styles.text}>
                  {s.title}
                </Text>
              </div>
              {s.description && (
                <Text fontStyle="italic" opacity={0.8}>
                  {s.description}
                </Text>
              )}
              {s.thinking && <ThinkingBubble text={s.thinking} bare />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export type TranscriptMessagesProps = {
  transcript: GlobalAiTranscriptMessage[];
  messagesById: Map<string, GlobalAiTranscriptMessage>;
  stampedOfferKindsByTargetId: ReadonlyMap<string, ReadonlySet<OfferKind>>;
  /**
   * Launch key of the report offer currently opening the report builder
   * (`buildReportOfferLaunchKey(messageId, reportType)`), or null. The clicked
   * inline trigger pulses while its launch is in flight, and all report
   * triggers are inert until it settles.
   */
  reportOfferLaunchingKey: string | null;
  isTurnRunning: boolean;
  isCommandInFlight: boolean;
  lastUserMessageId?: string;
  lastUserMessageRef: React.MutableRefObject<HTMLDivElement | null>;
  transcriptScrollRoot: HTMLDivElement | null;
  userTabs: Tab[];
  fireNavOffer: (
    navMsg: Extract<GlobalAiTranscriptMessage, { kind: 'nav_confirm' }>
  ) => void;
  fireBuildOffer: (
    buildMsg: Extract<
      GlobalAiTranscriptMessage,
      { kind: 'create_page_confirm' }
    >
  ) => void;
  fireStandardPageOffer: (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => void;
  /**
   * Fires the inline `offer:geo_tax` narration trigger. The id is the one the
   * narration STAMPED (artifact-derived), so this never has to trust the
   * clicked phrase.
   */
  fireGeoTaxOffer: (
    nextTaxonomyId: number,
    prompt?: string,
    reaskOnly?: boolean
  ) => void;
  getStandardPageOfferState: (
    chartMsg: Extract<GlobalAiTranscriptMessage, { kind: 'single_chart' }>
  ) => {
    match: GlobalAssistantSuggestMatch;
    access: GlobalAssistantAccessDecision;
  } | null;
  /**
   * Fires the report-builder launch for an inline `offer:report` narration
   * trigger (the only report-offer surface now that the card is gone).
   */
  onReportOfferClick: (args: {
    messageId: string;
    offer: ReportOfferCardFields;
    prompt?: string;
  }) => void;
};

export function TranscriptMessages(props: TranscriptMessagesProps) {
  const {
    transcript,
    messagesById,
    stampedOfferKindsByTargetId,
    reportOfferLaunchingKey,
    isTurnRunning,
    isCommandInFlight,
    lastUserMessageId,
    lastUserMessageRef,
    transcriptScrollRoot,
    userTabs,
    fireNavOffer,
    fireBuildOffer,
    fireStandardPageOffer,
    fireGeoTaxOffer,
    getStandardPageOfferState,
    onReportOfferClick,
  } = props;

  // Mid-turn, EVERY offer trigger is inert — each `fire*` handler below opens
  // with this same guard and bare-returns. The chips express it as
  // `isDisabled`; the inline narration triggers rendered a live-looking
  // underlined span with `cursor: pointer` and said nothing on click, so a
  // click landing on the tail of a streaming turn read as a broken link. Worse,
  // an inline trigger SUPPRESSES its chip (see `stampedOfferKindsByTargetId`
  // below), so where one exists it is the only affordance and the disabled
  // state was lost entirely. One flag, applied to all three trigger kinds.
  const offersInert = isTurnRunning || isCommandInFlight;

  // Fire the report offer: the exact action the card's "Open report builder"
  // button invokes, shared by the inline narration trigger. Mirrors the other
  // fire handlers' mid-stream guard, plus the cards' launch guard: while a
  // report launch is in flight (prefill extraction + navigation), all report
  // triggers are inert so a click can't double-launch.
  const fireReportOffer = (
    targetMsg: Extract<GlobalAiTranscriptMessage, { kind: 'report_offers' }>,
    offer: ReportOfferCardFields
  ) => {
    if (
      isTurnRunning ||
      isCommandInFlight ||
      reportOfferLaunchingKey !== null
    ) {
      return;
    }
    onReportOfferClick({
      messageId: targetMsg.id,
      offer,
      prompt: targetMsg.prompt,
    });
  };

  // Access can change after an offer is saved, and the click refuses a page
  // the account cannot open.
  const canOpenNavOffer = (optionId: string): boolean => {
    const match = getStandardPageMatchForOptionId(optionId, {});
    return Boolean(
      match &&
        getGlobalAssistantRouteAccess(match.navigatePath, userTabs).allowed
    );
  };

  const renderMessage = (
    msg: GlobalAiTranscriptMessage,
    previousMsg: GlobalAiTranscriptMessage | undefined
  ) => {
    const showAvatar = !previousMsg || previousMsg.role === 'user';

    // Resolve an inline trigger's backing message from the narration's stamp.
    // Absent/mismatched stamp → undefined → the marker renders as plain text.
    const stampedTarget = <
      K extends
        | 'nav_confirm'
        | 'create_page_confirm'
        | 'single_chart'
        | 'report_offers',
    >(
      textMsg: Extract<GlobalAiTranscriptMessage, { kind: 'text' }>,
      offer: OfferKind,
      kind: K
    ): Extract<GlobalAiTranscriptMessage, { kind: K }> | undefined => {
      const targetId = textMsg.offerTargets?.[offer];
      const target = targetId ? messagesById.get(targetId) : undefined;
      return target &&
        target.role === 'assistant' &&
        'kind' in target &&
        target.kind === kind
        ? (target as Extract<GlobalAiTranscriptMessage, { kind: K }>)
        : undefined;
    };

    if (msg.role === 'user') {
      return (
        <div
          className={layoutStyles.globalAiUserMessage}
          key={msg.id}
          ref={msg.id === lastUserMessageId ? lastUserMessageRef : undefined}
          // Lets the PW probe runner subtract the echoed prompt so assertions
          // read only assistant output (support/assistant-eval-runner.ts).
          data-testid="global-ai-user-message"
        >
          <div
            className={classNames(
              styles.userBubble,
              layoutStyles.userBubblePadding
            )}
          >
            <Text className={`${styles.text} ${styles.bodyText}`}>
              {msg.text}
            </Text>
          </div>
        </div>
      );
    }

    if (msg.kind === 'text') {
      // A single-value answer offers its standard page ONLY through the inline
      // offer:page trigger inside this narration, resolved from the narration's
      // stamp. No adjacency fallback: a narration carrying no page marker makes
      // no page offer, and nothing stands in for it — anything that did would
      // contradict the text on a refusal, pointing at the very page the refusal
      // said it could not scope.
      const pageTargetChart = stampedTarget(msg, 'page', 'single_chart');
      // Marker scan only where a stamp could wire it: `hasOfferMarker` re-runs
      // the offer regex over the whole narration, and this render path is hot
      // (every text bubble, every streamed flush).
      const pageAccess =
        pageTargetChart && hasOfferMarker(msg.text, 'page')
          ? getStandardPageOfferState(pageTargetChart)?.access
          : undefined;
      const pageOfferLive = !!pageAccess?.allowed;
      // Offered but not openable → the marker degrades to plain text above, so
      // explain the entitlement gap rather than leaving a named-but-inert page.
      const answerStandardPageUpsell =
        pageAccess && !pageAccess.allowed ? (
          <GlobalAssistantPermissionUpsell
            access={pageAccess}
            data-testid="global-ai-single-chart-standard-page-upsell"
          />
        ) : null;
      return (
        <div className={layoutStyles.answerMessage} key={msg.id}>
          <div className={layoutStyles.answerRow}>
            <AssistantAvatarSlot show={showAvatar} />
            <Text
              className={`${styles.text} ${styles.bodyText}`}
              flex={1}
              minW={0}
              whiteSpace="pre-wrap"
            >
              {/*
               * Trimmed because of `pre-wrap` above: the loop hands the model's
               * streamed text through untrimmed (global-ai-turn-loop.ts — the
               * `.trim()` guards there only decide WHETHER to emit, they do not
               * rewrite the string), so a stray leading/trailing newline used to
               * collapse and now would render as a visible blank line — which
               * reads as a rendering bug rather than a model artifact. Only the
               * outer edges are touched; interior newlines are the whole point
               * of pre-wrap, and offer markers are unaffected (a narration
               * ending on `[Data Dictionary](offer:nav)` still parses to a
               * trailing offer segment).
               */}
              {parseOfferSegments(msg.text.trim()).map((seg, segIdx) => {
                if (seg.type === 'text') {
                  return <span key={segIdx}>{seg.text}</span>;
                }
                if (seg.offer === 'page') {
                  // Inline single-value page offer. Wire to the preceding
                  // single-value chart's standard page; when there's no live
                  // offer (no chart / no access), render plain text — the
                  // no-access case is explained by the upsell line below.
                  if (!pageOfferLive || !pageTargetChart) {
                    return downgradedOfferSpan(msg.id, seg, segIdx);
                  }
                  return (
                    <Text
                      as="span"
                      key={segIdx}
                      data-testid="offer-trigger-page"
                      role="button"
                      tabIndex={offersInert ? -1 : 0}
                      aria-disabled={offersInert}
                      className={styles.text}
                      textDecoration="underline"
                      cursor={offersInert ? 'default' : 'pointer'}
                      opacity={offersInert ? 0.5 : undefined}
                      onClick={() => fireStandardPageOffer(pageTargetChart)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          fireStandardPageOffer(pageTargetChart);
                        }
                      }}
                    >
                      {seg.text}
                    </Text>
                  );
                }
                if (seg.offer === 'custom' || seg.offer === 'filters') {
                  // Neither offer has a surface: the post-chart custom-page
                  // offer and the same-page apply-filters chip were both
                  // removed. The explicit branch keeps a stray marker of either
                  // kind out of the nav/build fallback below, which keys off
                  // `offerTargets.build` and would otherwise wire the phrase to
                  // a "build a dashboard" click.
                  return downgradedOfferSpan(msg.id, seg, segIdx);
                }
                if (seg.offer === 'geo_tax') {
                  // The account-wide geography taxonomy switch. Its target is
                  // the taxonomy id the turn's `filter_response` artifact
                  // named, stamped in `offerTargets.geo_tax` — NEVER the
                  // model's visible phrase, which is free text. No stamp
                  // (no mismatch this turn, or a rehydrated/stale transcript)
                  // means the words render as plain prose: the substitution
                  // was still disclosed, there is just nothing to click.
                  //
                  // The explicit branch also keeps this marker out of the
                  // nav/build fallback below, which keys off
                  // `offerTargets.build` and would otherwise wire the
                  // disclosure phrase to a "build a dashboard" click.
                  const nextGeoTaxonomyId = parseGeoTaxOfferTargetId(
                    msg.offerTargets?.geo_tax
                  );
                  if (nextGeoTaxonomyId === undefined) {
                    return downgradedOfferSpan(msg.id, seg, segIdx);
                  }
                  return (
                    <Text
                      as="span"
                      key={segIdx}
                      data-testid={GEO_TAX_OFFER_TRIGGER_TESTID}
                      role="button"
                      // Same `offersInert` treatment as every other inline
                      // trigger. `fireGeoTaxOffer` bare-returns mid-turn behind
                      // the same guard, and this marker SUPPRESSES no chip — it
                      // is the only affordance there is — so without this it
                      // rendered as a live underlined link that silently did
                      // nothing. Observed as a dead click on the older of two
                      // switch offers while the newer turn's apply was still in
                      // flight (2026-09-01).
                      tabIndex={offersInert ? -1 : 0}
                      aria-disabled={offersInert}
                      className={styles.text}
                      textDecoration="underline"
                      cursor={offersInert ? 'default' : 'pointer'}
                      opacity={offersInert ? 0.5 : undefined}
                      onClick={() =>
                        fireGeoTaxOffer(
                          nextGeoTaxonomyId,
                          msg.prompt,
                          msg.geoTaxReask
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          fireGeoTaxOffer(
                            nextGeoTaxonomyId,
                            msg.prompt,
                            msg.geoTaxReask
                          );
                        }
                      }}
                    >
                      {seg.text}
                    </Text>
                  );
                }
                if (seg.offer === 'report') {
                  const targetMsg = stampedTarget(
                    msg,
                    'report',
                    'report_offers'
                  );
                  // Opens by the marker's report type, never its words.
                  const offer = targetMsg
                    ? matchReportOfferByMarker(
                        targetMsg.offers,
                        seg.target,
                        untargetedReportPosition(msg.text, segIdx)
                      )
                    : undefined;
                  if (!targetMsg || !offer) {
                    // No backing offer for this marker — plain text, never a
                    // dead trigger (mirrors the nav/build fallback).
                    return downgradedOfferSpan(msg.id, seg, segIdx);
                  }
                  // Launch feedback: the clicked trigger pulses while the
                  // prefill + report-builder navigation is in flight (the
                  // cards' isLoading equivalent); fireReportOffer makes every
                  // report trigger inert until the launch settles.
                  const isLaunching =
                    reportOfferLaunchingKey ===
                    buildReportOfferLaunchKey(targetMsg.id, offer.reportType);
                  // The clicked trigger shows progress; every trigger goes
                  // inert (mid-turn, or while any report launch is settling).
                  let reportTriggerCursor: 'progress' | 'default' | 'pointer' =
                    'pointer';
                  if (isLaunching) {
                    reportTriggerCursor = 'progress';
                  } else if (offersInert) {
                    reportTriggerCursor = 'default';
                  }
                  return (
                    <Text
                      as="span"
                      key={segIdx}
                      data-testid="offer-trigger-report"
                      role="button"
                      tabIndex={
                        offersInert || reportOfferLaunchingKey !== null ? -1 : 0
                      }
                      aria-busy={isLaunching}
                      aria-disabled={
                        offersInert || reportOfferLaunchingKey !== null
                      }
                      className={styles.text}
                      textDecoration="underline"
                      cursor={reportTriggerCursor}
                      opacity={offersInert ? 0.5 : undefined}
                      animation={
                        isLaunching
                          ? `${reportTriggerLaunchPulse} 1.1s ease-in-out infinite`
                          : undefined
                      }
                      onClick={() => fireReportOffer(targetMsg, offer)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          fireReportOffer(targetMsg, offer);
                        }
                      }}
                    >
                      {seg.text}
                    </Text>
                  );
                }
                const offerMsg =
                  seg.offer === 'nav'
                    ? stampedTarget(msg, 'nav', 'nav_confirm')
                    : stampedTarget(msg, 'build', 'create_page_confirm');
                if (
                  !offerMsg ||
                  (offerMsg.kind === 'nav_confirm' &&
                    !canOpenNavOffer(offerMsg.optionId))
                ) {
                  // No backing offer, or a page the account can no longer open
                  // (its chip carries the upsell): plain text, never a dead trigger.
                  return downgradedOfferSpan(msg.id, seg, segIdx);
                }
                return (
                  <Text
                    as="span"
                    key={segIdx}
                    data-testid={
                      offerMsg.kind === 'nav_confirm'
                        ? 'offer-trigger-nav'
                        : 'offer-trigger-build'
                    }
                    role="button"
                    tabIndex={offersInert ? -1 : 0}
                    aria-disabled={offersInert}
                    className={styles.text}
                    textDecoration="underline"
                    cursor={offersInert ? 'default' : 'pointer'}
                    opacity={offersInert ? 0.5 : undefined}
                    onClick={() =>
                      offerMsg.kind === 'nav_confirm'
                        ? fireNavOffer(offerMsg)
                        : fireBuildOffer(offerMsg)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (offerMsg.kind === 'nav_confirm') {
                          fireNavOffer(offerMsg);
                        } else {
                          fireBuildOffer(offerMsg);
                        }
                      }
                    }}
                  >
                    {seg.text}
                  </Text>
                );
              })}
            </Text>
          </div>
          {answerStandardPageUpsell}
        </div>
      );
    }

    if (msg.kind === 'clarification') {
      return (
        <div className={layoutStyles.assistantRow} key={msg.id}>
          <AssistantAvatarSlot show={showAvatar} />
          <Text
            className={`${styles.text} ${styles.bodyText}`}
            flex={1}
            minW={0}
          >
            {msg.text}
          </Text>
        </div>
      );
    }

    if (msg.kind === 'error') {
      return (
        <div className={layoutStyles.errorMessage} key={msg.id}>
          <Callout
            variant="error"
            className={classNames(styles.alert, styles.compactAlert)}
            title="Something went wrong"
            description={msg.text}
          />
        </div>
      );
    }

    if (msg.kind === 'stopped') {
      return (
        <div className={layoutStyles.stoppedMessage} key={msg.id}>
          <Text
            data-testid="global-ai-assistant-stopped"
            fontSize="11px"
            fontStyle="italic"
            className={styles.textMuted}
          >
            Stopped
          </Text>
        </div>
      );
    }

    if (msg.kind === 'nav_confirm') {
      // The `navigate` tool is terminal: it never navigates. This chip / inline
      // trigger is the ONLY way the assistant navigates, and only on an explicit
      // click (hard rule). Mirror the single-chart standard-page access gate so
      // the chip is disabled + upsold when the account can't open the
      // destination.
      const navMatch = getStandardPageMatchForOptionId(msg.optionId, {});
      const access = navMatch
        ? getGlobalAssistantRouteAccess(navMatch.navigatePath, userTabs)
        : null;
      const canNavigate = Boolean(navMatch && access?.allowed);
      // Suppressed when a narration stamped THIS chip's id for `nav` (and
      // carried the marker) AND that stamp is renderable — the inline trigger
      // replaced it. A page the account can't open renders no trigger, so its
      // chip stays to carry the upsell.
      if (stampedOfferKindsByTargetId.get(msg.id)?.has('nav') && canNavigate) {
        return null;
      }
      // No auto-navigation anywhere: the chip renders as the manual fallback on
      // every page (home and off-home alike) when no inline trigger is present.
      return (
        <div className={layoutStyles.assistantRow} key={msg.id}>
          <AssistantAvatarSlot show={showAvatar} />
          <div className={layoutStyles.offerActions}>
            <div className={layoutStyles.offerChip}>
              <Button
                variant="bare"
                className={styles.chip}
                data-testid="global-ai-nav-confirm"
                disabled={!canNavigate || isTurnRunning || isCommandInFlight}
                onClick={
                  canNavigate && navMatch ? () => fireNavOffer(msg) : undefined
                }
              >
                {access && !access.allowed
                  ? `No access to ${access.featureLabel}`
                  : `Go to ${msg.pageName}`}
              </Button>
              {access && !access.allowed ? (
                <GlobalAssistantPermissionUpsell
                  access={access}
                  data-testid="global-ai-nav-confirm-upsell"
                />
              ) : null}
            </div>
          </div>
        </div>
      );
    }

    if (msg.kind === 'create_page_confirm') {
      // `create_custom_page` is terminal: it never builds. This chip / inline
      // trigger is the ONLY way the assistant builds + opens a custom dashboard,
      // and only on an explicit click — mirroring the nav-confirm hard rule.
      // Gate on the same Custom Dash access decision the single-chart "Open as
      // custom page" buttons use, so the chip is disabled + upsold when the
      // account lacks it.
      // Suppress the chip when the same turn's narration already carries a
      // RENDERABLE inline `offer:build` trigger stamped with this chip's id;
      // otherwise render the chip as the fallback.
      if (stampedOfferKindsByTargetId.get(msg.id)?.has('build')) {
        return null;
      }
      return (
        <div className={layoutStyles.assistantRow} key={msg.id}>
          <AssistantAvatarSlot show={showAvatar} />
          <div className={layoutStyles.offerActions}>
            <div className={layoutStyles.offerChip}>
              <Button
                variant="bare"
                className={styles.chip}
                data-testid="global-ai-create-page-confirm"
                disabled={isTurnRunning || isCommandInFlight}
                onClick={() => fireBuildOffer(msg)}
              >
                Build custom dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (msg.kind === 'report_offers') {
      // The report OFFER CARD is gone (product decision 2026-07-08): reports are
      // offered ONLY as inline [Name](offer:report:<reportType>) narration links. This
      // message is kept purely as the DATA CARRIER those inline triggers resolve
      // against (`offerTargets.report` -> this id -> `msg.offers` ->
      // `matchReportOfferByMarker`), so it renders nothing itself.
      return null;
    }

    if (msg.kind === 'single_chart') {
      // A single-value answer renders headlessly as text (no chart card), the
      // same render path as a genuine text chart.
      const isTextChart =
        msg.item.plot?.chartType === 'text' || msg.answerAsText === true;

      if (isTextChart) {
        if (msg.answerAsText) {
          // Single-value answer: render ONLY the invisible (1px, headless)
          // preview slot so the chart can fetch the figure. The answer arrives
          // as the next text message, which also carries the whole page offer —
          // the inline offer:page trigger, and the entitlement line when that
          // page is named but not openable (see the `text` branch).
          return (
            <div className={layoutStyles.textChartSlot} key={msg.id}>
              <GlobalAIAssistantChartPreviewSlot
                messageId={msg.id}
                intersectionRoot={transcriptScrollRoot}
                variant="text"
              />
            </div>
          );
        }
        return (
          <div className={layoutStyles.chartMessage} key={msg.id}>
            <div className={layoutStyles.chartRow}>
              <AssistantAvatarSlot show={showAvatar} />
              <div className={layoutStyles.textChartBody}>
                <GlobalAIAssistantChartPreviewSlot
                  messageId={msg.id}
                  intersectionRoot={transcriptScrollRoot}
                  variant="text"
                />
              </div>
            </div>
          </div>
        );
      }

      return (
        <div className={layoutStyles.chartMessage} key={msg.id}>
          {msg.intentSummary ? (
            <div className={layoutStyles.intentSummary}>
              <AssistantAvatarSlot show={showAvatar} />
              <Text
                fontSize="var(--ui-font-size-x-sm)"
                lineHeight="15px"
                className={styles.text}
                flex={1}
                minW={0}
              >
                {msg.intentSummary}
              </Text>
            </div>
          ) : null}
          {/* Keep the chart inside the avatar-gutter column so it lines up
              with the narration text above/below instead of spilling left
              under the assistant icon. */}
          <div className={layoutStyles.chartRow}>
            <AssistantAvatarSlot show={showAvatar && !msg.intentSummary} />
            <div className={layoutStyles.messageBody}>
              <GlobalAIAssistantChartPreviewSlot
                messageId={msg.id}
                intersectionRoot={transcriptScrollRoot}
              />
              {/* Nothing is appended under a rendered chart: the page behind
                  it is offered ONLY by the inline offer:page trigger in the
                  narration, and the entitlement line for a named-but-unopenable
                  page renders under that same narration. */}
            </div>
          </div>
        </div>
      );
    }

    if (msg.kind === 'reasoning_trace') {
      // Skip empty finished traces (single text-only turn with no steps).
      if (msg.trace.steps.length === 0 && msg.trace.endedAt !== undefined) {
        return null;
      }
      return (
        <div className={layoutStyles.assistantRow} key={msg.id}>
          <AssistantAvatarSlot show={showAvatar} />
          <div className={layoutStyles.messageBody}>
            <ReasoningTraceView trace={msg.trace} />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {transcript.map((msg, index) =>
        renderMessage(msg, transcript[index - 1])
      )}
    </>
  );
}
